/**
 * Mascot — Notion-style character mascots (a book, a bulb, an apple... with
 * eyes). Each sits on a colored circular chip, blinks on a loop, and floats.
 * Used on the landing page around the hero mockup and in feature sections.
 */
import React from 'react';

export type MascotName = 'book' | 'bulb' | 'apple' | 'clock' | 'folder' | 'star';

const INK = '#1f2937';

/** Pair of blinking eyes. Delay staggers blinks so characters feel alive. */
const Eyes: React.FC<{ cx1: number; cx2: number; cy: number; delay?: number; r?: number }> = ({
  cx1,
  cx2,
  cy,
  delay = 0,
  r = 1.7,
}) => (
  <>
    <ellipse className="mascot-eye" style={{ animationDelay: `${delay}s` }} cx={cx1} cy={cy} rx={r} ry={r * 1.25} fill={INK} stroke="none" />
    <ellipse className="mascot-eye" style={{ animationDelay: `${delay}s` }} cx={cx2} cy={cy} rx={r} ry={r * 1.25} fill={INK} stroke="none" />
  </>
);

const smile = (d: string) => <path d={d} stroke={INK} strokeWidth={2} strokeLinecap="round" fill="none" />;

/** Character artwork, 48x48 viewBox. `blinkDelay` staggers eye blinks. */
const ART: Record<MascotName, (blinkDelay: number) => React.ReactNode> = {
  book: (d) => (
    <>
      {/* closed book, cover facing us, pages on the right */}
      <rect x="11" y="9" width="25" height="31" rx="3.5" fill="#ffffff" stroke={INK} strokeWidth={2.4} />
      <path d="M16 9v31" stroke={INK} strokeWidth={2.2} />
      <Eyes cx1={22.5} cx2={30.5} cy={22} delay={d} />
      {smile('M23.5 28.5c1.6 1.8 4.4 1.8 6 0')}
    </>
  ),
  bulb: (d) => (
    <>
      <path
        d="M24 7a11.5 11.5 0 0 0-6.8 20.8c1.3 1 2.1 2.5 2.1 4.2h9.4c0-1.7.8-3.2 2.1-4.2A11.5 11.5 0 0 0 24 7z"
        fill="#fef3c7"
        stroke={INK}
        strokeWidth={2.4}
      />
      <path d="M19.8 35.5h8.4M20.8 39h6.4" stroke={INK} strokeWidth={2.2} strokeLinecap="round" />
      {/* little rays */}
      <path d="M8.5 12l3 2M39.5 12l-3 2M24 2.5v3" stroke={INK} strokeWidth={2} strokeLinecap="round" />
      <Eyes cx1={20} cx2={28} cy={18} delay={d} />
      {smile('M20.8 23.5c1.5 1.7 4.9 1.7 6.4 0')}
    </>
  ),
  apple: (d) => (
    <>
      <path
        d="M24 14.5c-2.5-2-6.5-2.3-9.3-.3-4 2.8-4.7 9.4-1.7 15.6 2.3 4.8 6.2 8.4 9.4 7.6.5-.1 1.1-.1 1.6 0 3.2.8 7.1-2.8 9.4-7.6 3-6.2 2.3-12.8-1.7-15.6-2.8-2-6.8-1.7-9.3.3z"
        fill="#ffffff"
        stroke={INK}
        strokeWidth={2.4}
      />
      <path d="M24 14.5c0-3 1.5-5.5 4-7" stroke={INK} strokeWidth={2.2} strokeLinecap="round" />
      <path d="M28 9c2.5-1 5-.5 6.5 1-1.5 1.5-4 2-6.5 1z" fill="#86efac" stroke={INK} strokeWidth={2} />
      <Eyes cx1={20} cx2={28} cy={25} delay={d} />
      {smile('M20.8 30.5c1.5 1.7 4.9 1.7 6.4 0')}
    </>
  ),
  clock: (d) => (
    <>
      <circle cx="24" cy="26" r="14" fill="#ffffff" stroke={INK} strokeWidth={2.4} />
      {/* bells + feet */}
      <path d="M12.5 13.5l-4-3.5M35.5 13.5l4-3.5" stroke={INK} strokeWidth={2.4} strokeLinecap="round" />
      <path d="M9.5 8.5a4.5 4.5 0 0 1 6 .5M38.5 8.5a4.5 4.5 0 0 0-6 .5" stroke={INK} strokeWidth={2.4} strokeLinecap="round" />
      <path d="M16 38.5l-2 3M32 38.5l2 3" stroke={INK} strokeWidth={2.4} strokeLinecap="round" />
      <Eyes cx1={19.5} cx2={28.5} cy={23} delay={d} />
      {smile('M20.5 29c1.6 1.8 5.4 1.8 7 0')}
    </>
  ),
  folder: (d) => (
    <>
      <path
        d="M8.5 14a3 3 0 0 1 3-3h8l3.5 4h13.5a3 3 0 0 1 3 3v17a3 3 0 0 1-3 3h-25a3 3 0 0 1-3-3V14z"
        fill="#ffffff"
        stroke={INK}
        strokeWidth={2.4}
      />
      <Eyes cx1={20} cx2={28} cy={25} delay={d} />
      {smile('M20.8 30.5c1.5 1.7 4.9 1.7 6.4 0')}
    </>
  ),
  star: (d) => (
    <>
      <path
        d="M24 6l5.2 10.6 11.6 1.7-8.4 8.2 2 11.6L24 32.6l-10.4 5.5 2-11.6-8.4-8.2 11.6-1.7z"
        fill="#fde68a"
        stroke={INK}
        strokeWidth={2.4}
        strokeLinejoin="round"
      />
      <Eyes cx1={20.5} cx2={27.5} cy={22} delay={d} />
      {smile('M21 27.5c1.3 1.5 4.7 1.5 6 0')}
    </>
  ),
};

interface MascotProps {
  name: MascotName;
  /** Chip background color; the character itself keeps its own palette. */
  chip?: string;
  className?: string;
  /** Seconds; staggers the blink loop so mascots don't blink in unison. */
  blinkDelay?: number;
}

const Mascot: React.FC<MascotProps> = ({ name, chip = '#fbbf24', className = 'h-16 w-16', blinkDelay = 0 }) => (
  <div
    className={`flex items-center justify-center rounded-full ${className}`}
    style={{ backgroundColor: chip }}
    aria-hidden="true"
  >
    <svg viewBox="0 0 48 48" className="h-[72%] w-[72%]" fill="none">
      {ART[name](blinkDelay)}
    </svg>
  </div>
);

export default Mascot;
